const components = {}
let domContentAlreadyLoaded = false

document.addEventListener('DOMContentLoaded', () => { domContentAlreadyLoaded = true })

/**
   * Will scope the components script to the DOM element of the component.
   *
   * @param {string} name The name of the component
   * @param {element} component The component DOM element
   * @param {string} triggerFuncName Name of a potential window function
   * @param {function} consentFunc Function that is executed if the element is blocked by usercentrics
   * @param {string} consentServiceId ID of the usercentrics service for the given component
   */
const register = (name, component, triggerFuncName, consentFunc, consentServiceId) => {
  components[name] = component

  // if the component should be triggered by a window function (e.g. google maps callback)
  if (triggerFuncName) {
    window[triggerFuncName] = () => {
      // trigger immediately if the DOM is already loaded
      if (domContentAlreadyLoaded) {
        apply(name)
        return
      }

      // add DOM listener when not loaded yet
      document.addEventListener('DOMContentLoaded', () => {
        apply(name)
      })
    }
  } else {
    // apply the element regularly
    document.addEventListener('DOMContentLoaded', () => {
      apply(name)
    })
  }

  // if the component has a special function that is executed while its blocked by usercentrics
  if (consentFunc && consentServiceId) {
    // wait for usercentrics to be initialized
    window.addEventListener('UC_UI_INITIALIZED', function (event) {
      // check if the service/component is allowed or blocked by the user
      const isAllowed = window.UC_UI.getServicesBaseInfo().find(service => service.id === consentServiceId).consent.status
      if (!isAllowed) {
        document.querySelectorAll(`[data-component=${name}]`).forEach(el => consentFunc(el, true))
      }
    })
  }
}

/**
   * Loads the prefix of the component. Requires data-prefix inside DOM.
   *
   * @param {element} component The component element
   * @return {string} The prefix of the element
   */
const getPrefix = component => component.dataset.prefix

const apply = (name, scope = document) => {
  scope.querySelectorAll(`[data-component=${name}]`).forEach(el => components[name](el))
}

const applyAll = (scope = document) => {
  Object.keys(components).forEach(name => {
    apply(name, scope)
  })
}

/**
   * Will attach scripts from an array (scripts) to the beginning of an element.
   * It is feasible for big libraries that should only be loaded on special pages.
   *
   * @param {array} scripts The array of script paths
   * @param {element} el The element where the scripts will be attached to
   * @param {function} attachRecall The function that should be called on success
   */
const attachScripts = (scripts, el, attachRecall) => {
  const attachPromises = (src, position) => {
    return new Promise(function (resolve, reject) {
      const script = document.createElement('script')
      script.src = src
      script.onload = resolve
      script.onerror = reject
      el.insertBefore(script, el.childNodes[position])
    })
  }

  let result = scripts.reduce((accumulatorPromise, currentValue, index) => {
    return accumulatorPromise.then(() => {
      return attachPromises(currentValue, index)
    })
  }, Promise.resolve())

  result
    .then(attachRecall)
    .catch((e) => {
      console.log('Error on attaching scripts: ', e)
    })
}

/**
   * Checks if the provided element is visible in the viewport. Returns true when it is.
   *
   * @param {element} el The element that is checked
   */
const isInViewport = (el) => {
  var bounding = el.getBoundingClientRect()
  return (
    bounding.top >= 0 &&
    bounding.left >= 0 &&
    bounding.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
    bounding.right <= (window.innerWidth || document.documentElement.clientWidth)
  )
}

/**
   * Checks if the used device supports touch gestures
   */
const isTouchDevice = () => {
  return (
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    navigator.msMaxTouchPoints > 0
  )
}

const getCookie = function (cookieName) {
  const value = '; ' + document.cookie
  const parts = value.split('; ' + cookieName + '=')
  return parts.length === 2 ? parts.pop().split(';').shift() : false
}

const unsetCookie = function (cookieName) {
  document.cookie = cookieName + '=false; expires=Thu, 01 Jan 1970 00:00:01 GMT; Max-Age=0; path=/; SameSite=lax; Secure'
}

/**
 * Rounds to one decimal digit, mathematically correct (in contrast to only using .toFixed(1)). Cuts if decimal digit is 0.
 *
 * @param {number} inputValue The number that will be rounded
 * @return {number} Rounded number, optionally with 1 decimal digit
 */
const roundOneDigit = inputValue => {
  return (Math.round(inputValue * 100) / 100).toFixed(1).replace(/[.,]0$/, '')
}

export {
  register,
  getPrefix,
  attachScripts,
  isInViewport,
  isTouchDevice,
  apply,
  applyAll,
  getCookie,
  unsetCookie,
  roundOneDigit
}

// put this somewhere else and add comments what this is doing
var isChrome = /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor)
if (window.location.hash && isChrome) {
  setTimeout(function () {
    var hash = window.location.hash
    window.location.hash = ""
    window.location.hash = hash
  }, 300)
}
